
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { handleOAuthCallback } from '@/utils/authUtils';

export function useOAuthHandler() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleOAuthRedirect = async (): Promise<void> => {
    try {
      console.log("Starting OAuth redirect handling...");
      const callbackData = await handleOAuthCallback();
      
      console.log("OAuth callback data:", callbackData);
      
      if (!callbackData) {
        // Just check for session but don't return it
        console.log("No callback data, checking for session...");
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error("Error getting session:", error);
          throw error;
        }
        console.log("Session check result:", data.session ? "Session exists" : "No session");
        return;
      }

      if (callbackData.error) {
        console.error("OAuth callback error:", callbackData.error, callbackData.errorDescription);
        throw new Error(callbackData.errorDescription || 'Authentication error');
      }

      if (callbackData.type === 'hash' && callbackData.accessToken && callbackData.refreshToken) {
        console.log("Processing hash-based OAuth callback with tokens");
        const { error } = await supabase.auth.setSession({
          access_token: callbackData.accessToken,
          refresh_token: callbackData.refreshToken,
        });
        
        if (error) {
          console.error("Error setting session:", error);
          throw error;
        }
        
        // Verify session was set
        const { data: sessionData } = await supabase.auth.getSession();
        console.log("Session after setting:", sessionData.session ? "Set successfully" : "Failed to set");
        
        if (sessionData.session) {
          // Check for redirect parameter
          const urlParams = new URLSearchParams(window.location.search);
          const redirectUrl = urlParams.get('redirect');
          
          if (redirectUrl) {
            navigate(redirectUrl);
          } else {
            navigate("/dashboard");
          }
          
          toast({
            title: "Signed in successfully",
            description: "Welcome back!",
          });
        }
      }
      
      if (callbackData.type === 'search' && callbackData.code) {
        console.log("Processing search-based OAuth callback with code");
        // The PKCE flow will automatically exchange the code for a session
        const { data: sessionData, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting session after code auth:", error);
          throw error;
        }
        
        console.log("Session after code flow:", sessionData.session ? "Session exists" : "No session");
        
        if (sessionData.session) {
          // Check for redirect parameter
          const urlParams = new URLSearchParams(window.location.search);
          const redirectUrl = urlParams.get('redirect');
          
          if (redirectUrl) {
            navigate(redirectUrl);
          } else {
            navigate("/dashboard");
          }
          
          toast({
            title: "Signed in successfully",
            description: "Welcome back!",
          });
        } else {
          // Try to exchange the code manually
          console.log("No session after code flow, trying to exchange code manually");
          await supabase.auth.exchangeCodeForSession(callbackData.code);
          
          // Check session again
          const { data: newSessionData, error: newError } = await supabase.auth.getSession();
          if (newError) {
            console.error("Error getting session after manual exchange:", newError);
            throw newError;
          }
          
          console.log("Session after manual exchange:", newSessionData.session ? "Session exists" : "No session");
          
          if (newSessionData.session) {
            // Check for redirect parameter
            const urlParams = new URLSearchParams(window.location.search);
            const redirectUrl = urlParams.get('redirect');
            
            if (redirectUrl) {
              navigate(redirectUrl);
            } else {
              navigate("/dashboard");
            }
            
            toast({
              title: "Signed in successfully",
              description: "Welcome back!",
            });
          }
        }
      }
    } catch (err: any) {
      console.error("OAuth redirect handling error:", err);
      toast({
        title: "Authentication Error",
        description: err.message || "An unexpected error occurred during authentication.",
        variant: "destructive",
      });
    }
  };

  return { handleOAuthRedirect };
}
